import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App.jsx';
import reportWebVitals from './reportWebVitals';
import PropTypes from 'prop-types';
import { BrowserRouter, useNavigate } from 'react-router-dom';
import { OktaAuth } from '@okta/okta-auth-js';
import { Security } from '@okta/okta-react';
import { CONFIG } from './config/config.js';
import { oktaTokenManager } from './utils/oktaTokenManager.js';

const oktaAuth = new OktaAuth({
  issuer: CONFIG.OKTA_ISSUER,
  clientId: CONFIG.OKTA_CLIENT_ID,
  redirectUri: CONFIG.OKTA_REDIRECT_URI,
  scopes: CONFIG.OKTA_SCOPES,
  pkce: CONFIG.OKTA_PKCE,
});

// Initialize token manager with oktaAuth instance
oktaTokenManager.initialize(oktaAuth);

// Expose oktaAuth for quick debugging in DevTools
if (import.meta.env.DEV) {
  window.__oktaAuth = oktaAuth;
}

// Wrapper component to provide navigate to Security
function OktaSecurityWrapper({ children }) {
  const navigate = useNavigate();
  const [isOktaReady, setIsOktaReady] = React.useState(false);
  
  // Ensure Okta is started
  React.useEffect(() => {
    oktaAuth.start()
      .then(() => {
        // Verify authStateManager is ready
        if (!oktaAuth.authStateManager) {
          console.error('authStateManager is not available after start');
          setIsOktaReady(true);
          return;
        }
        
        // Force an update if authState is null
        const authState = oktaAuth.authStateManager.getAuthState();
        if (!authState) {
          oktaAuth.authStateManager.updateAuthState().catch(err => {
            console.error('Failed to update authState:', err);
          });
        }
        
        setIsOktaReady(true);
      })
      .catch((err) => {
        console.error('Okta Auth failed to start:', err);
        setIsOktaReady(true);
      });
  }, []);
  
  const restoreOriginalUri = async (_oktaAuth, originalUri) => {
    // Auth flow completed successfully — clear the PKCE redirect guard so the
    // next login attempt (e.g. after logout) can start a fresh PKCE flow.
    sessionStorage.removeItem('okta_pkce_redirect_in_progress');

    // Convert to relative URL and remove base path if present
    const baseUrl = import.meta.env.BASE_URL || '/';
    let targetPath = originalUri || baseUrl;
    
    // If it's a full URL, extract the pathname
    if (targetPath.startsWith('http')) {
      try {
        const url = new URL(targetPath);
        targetPath = url.pathname + url.search + url.hash;
      } catch (e) {
        console.error('Failed to parse originalUri:', e);
        targetPath = baseUrl;
      }
    }
    
    // Remove base path prefix if present
    if (baseUrl !== '/' && targetPath.startsWith(baseUrl)) {
      targetPath = targetPath.substring(baseUrl.length);
    }
    
    // Ensure it starts with /
    if (!targetPath.startsWith('/')) {
      targetPath = '/' + targetPath;
    }
    
    navigate(targetPath, { replace: true });
  };
  
  if (!isOktaReady) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <div>Initializing authentication...</div>
    </div>;
  }
  
  return (
    <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
      {children}
    </Security>
  );
}

OktaSecurityWrapper.propTypes = {
  children: PropTypes.node.isRequired
};

// Export for testing
export { OktaSecurityWrapper };

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
   <React.StrictMode>
    <BrowserRouter basename={import.meta.env.BASE_URL}>
      <OktaSecurityWrapper>
        <App />
      </OktaSecurityWrapper>
    </BrowserRouter>
   </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
